/**
 * The fixed-step frame loop (FS-02 §4.1). Accumulator with an 8-substep clamp; render interpolates
 * between the two most recent sim states with alpha = accumulator / FIXED_DT.
 *
 * Two facts that are easy to get wrong and are done right here:
 *  1. requestAnimationFrame is scheduled LAST, not first — so a throw stops the loop instead of
 *     logging once per frame forever (the 1.0 defect, CS-01 §6).
 *  2. The frame-delta clamp and the substep clamp are two separate guards.
 */
import { FIXED_DT, MAX_FRAME_DELTA, MAX_SUBSTEPS } from './constants';
import type { EventBus } from './events';
import type { EventMap } from './event-map';
import { GameState, type StateMachine } from './state-machine';

export interface Simulation {
  /** One fixed sub-step. `dt` is always FIXED_DT. */
  step(dt: number, simTick: number): void;
  /** Copy current transforms → previous, before a step, for render interpolation. */
  swapBuffers(): void;
}

export interface Renderer {
  render(alpha: number): void;
}

export interface GameLoop {
  start(): void;
  stop(): void;
  readonly running: boolean;
  readonly simTick: number;
  readonly alpha: number;
}

export interface LoopContext {
  bus: EventBus<EventMap>;
  machine: StateMachine;
}

export function createLoop(ctx: LoopContext, sim: Simulation, view: Renderer): GameLoop {
  let running = false;
  let rafHandle = 0;
  let lastFrameMs = 0;
  let accumulator = 0;
  let simTick = 0;

  const stallEvt = { rawDt: 0, clampedTo: 0, simTick: 0 };
  const satEvt = { discardedSeconds: 0, simTick: 0 };

  function frame(nowMs: number): void {
    rafHandle = 0;

    let dt = (nowMs - lastFrameMs) / 1000;
    lastFrameMs = nowMs;
    if (!Number.isFinite(dt) || dt < 0) dt = 0;

    if (dt > MAX_FRAME_DELTA) {
      stallEvt.rawDt = dt;
      stallEvt.clampedTo = MAX_FRAME_DELTA;
      stallEvt.simTick = simTick;
      ctx.bus.emit('loop:stall', stallEvt);
      dt = MAX_FRAME_DELTA;
    }

    if (ctx.machine.simulates()) accumulator += dt;

    let steps = 0;
    try {
      while (accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
        sim.swapBuffers();
        sim.step(FIXED_DT, simTick);
        ctx.machine.tick();
        accumulator -= FIXED_DT;
        simTick++;
        steps++;
      }
    } catch (err) {
      accumulator = 0;
      ctx.bus.emit('loop:fault', { error: err, simTick, state: ctx.machine.current });
      ctx.machine.request(GameState.Fault, 'sim-exception');
      if (import.meta.env.DEV) console.error('sim step threw:', err);
    }

    if (accumulator >= FIXED_DT) {
      const keep = accumulator % FIXED_DT;
      satEvt.discardedSeconds = accumulator - keep;
      satEvt.simTick = simTick;
      accumulator = keep;
      ctx.bus.emit('loop:saturated', satEvt);
    }

    ctx.machine.applyPending();
    ctx.bus.drain();

    const alpha = accumulator / FIXED_DT;
    view.render(alpha);

    if (running) rafHandle = requestAnimationFrame(frame);
  }

  return {
    start(): void {
      if (running) return;
      running = true;
      lastFrameMs = performance.now();
      accumulator = 0;
      rafHandle = requestAnimationFrame(frame);
    },
    stop(): void {
      running = false;
      if (rafHandle !== 0) {
        cancelAnimationFrame(rafHandle);
        rafHandle = 0;
      }
    },
    get running() {
      return running;
    },
    get simTick() {
      return simTick;
    },
    get alpha() {
      return accumulator / FIXED_DT;
    },
  };
}
